import classNames from "classnames";
import { action, computed } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";
import { PopperLayer } from "react-atmosphere";
import { PaneType, PaneTypeNames } from "../Types";
import { AnimatedPopout } from "../ui/AnimatedPopout";
import { ContextMenuItem } from "../ui/ContextMenu";
import { FeatherIcon } from "../ui/FeatherIcon";
import { Tooltip } from "../ui/Tooltip";
import { canvasDispatch } from "./CanvasDispatch";
import {
  canCreateNewPaneComputed,
  selectedToolAtom,
  undoDeletePaneStackAtom,
} from "./CanvasState";
import { ParentComlinkAPI, useClosePopperSandbox } from "./FrameSandboxAPI";
import { AddPaneMenuTypes, NewPaneContextMenuItem } from "./NewPaneContextMenu";
import { NewPaneIcon, PanHandIcon, PointerIcon } from "./SvgIcons";
import { showTemplateBrowser } from "./TemplateBrowserPane";

const AddPanePopupRootClassName =
  "relative border-t-2 flex flex-col border-secondary-100 bg-neutral-0 py-1.5 rounded-b shadow-xl min-w-36";

const AddPaneMenu = observer(
  ({
    onChange,
    onClose,
  }: {
    onChange: (paneType: PaneType) => void;
    onClose: () => void;
  }) => {
    const lastUndoAction = computed(
      () =>
        undoDeletePaneStackAtom.value[undoDeletePaneStackAtom.value.length - 1]
    ).get();

    return (
      <div className={AddPanePopupRootClassName}>
        {AddPaneMenuTypes.map(([paneType, tooltipText]) => (
          <NewPaneContextMenuItem
            tooltipText={tooltipText}
            onClick={() => {
              onChange(paneType);
              onClose();
            }}
            className={
              paneType === PaneType.Evaluate ? "justify-between" : undefined
            }
            key={paneType}
          >
            <span>
              {PaneTypeNames[paneType]} pane
              {paneType === PaneType.LLM ? (
                <span className="text-primary-300 text-2xs uppercase ml-1">
                  preview
                </span>
              ) : null}
            </span>
            {paneType === PaneType.Evaluate ? (
              <span className="text-neutral-400">E</span>
            ) : null}
          </NewPaneContextMenuItem>
        ))}
        <div className="border-t border-neutral-100 my-1" />
        <ContextMenuItem
          onClick={() => {
            showTemplateBrowser("toolbar add pane menu");
            onClose();
          }}
        >
          browse templates
        </ContextMenuItem>
        {lastUndoAction !== undefined ? (
          <ContextMenuItem
            onClick={action(() => {
              // keep rest of stack?
              undoDeletePaneStackAtom.value = [];
              canvasDispatch(lastUndoAction);
              onClose();
            })}
            color="custom"
            className="text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 active:bg-neutral-150"
          >
            <div className="flex items-center gap-1">
              <FeatherIcon icon="rotate-ccw" size="sm" />
              undo last delete
            </div>
          </ContextMenuItem>
        ) : null}
      </div>
    );
  }
);

export const ToolbarPointerButton = observer(
  ({ className }: { className: string }) => {
    const isSelected = computed(
      () => selectedToolAtom.value[0] === "pointer"
    ).get();
    return (
      <Tooltip
        content={
          <div className="flex gap-2">
            <span>select</span>
            <span className="text-neutral-400">V</span>
          </div>
        }
        side="bottom"
      >
        <button
          onClick={action(() => {
            selectedToolAtom.value = ["pointer"];
          })}
          className={classNames(
            "flex items-center justify-center w-10 md:w-11 text-neutral-600",
            isSelected ? "bg-secondary-100" : "hover:bg-neutral-50",
            className
          )}
        >
          <PointerIcon />
        </button>
      </Tooltip>
    );
  }
);

export const ToolbarPanButton = observer(() => {
  const isSelected = computed(() => selectedToolAtom.value[0] === "pan").get();
  return (
    <Tooltip
      content={
        <div>
          hold <span className="font-semibold">space</span> to pan
        </div>
      }
      side="bottom"
    >
      <button
        onClick={action(() => {
          selectedToolAtom.value = ["pan", undefined];
        })}
        className={classNames(
          "flex items-center justify-center w-10 md:w-11 text-neutral-600",
          isSelected ? "bg-secondary-100" : "hover:bg-neutral-50"
        )}
      >
        <PanHandIcon />
      </button>
    </Tooltip>
  );
});

export const ToolbarAddPaneButton = observer(
  ({ className }: { className?: string }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [showPopupState, setShowPopup] = useState(false);
    useClosePopperSandbox(setShowPopup);
    const selectedTool = selectedToolAtom.value;
    const isSelected = selectedTool[0] === "pane";
    const canCreatePane = canCreateNewPaneComputed.get();
    const showSwitchToMainLayoutMessage = !canCreatePane && isSelected;
    const showPopup = showPopupState || showSwitchToMainLayoutMessage;
    const [selectedPaneType, setSelectedPaneType] = useState<PaneType>(
      PaneType.Evaluate
    );
    if (selectedTool[0] === "pane" && selectedPaneType !== selectedTool[1]) {
      setSelectedPaneType(selectedTool[1]);
    }

    return (
      <div
        className={classNames(
          "relative flex group text-neutral-600",
          isSelected
            ? "bg-secondary-100"
            : showPopup
            ? "bg-secondary-50"
            : "hover:bg-neutral-50",
          className
        )}
        ref={rootRef}
      >
        <Tooltip
          content={
            canCreatePane ? (
              <div>
                <div>
                  <span className="font-semibold">drag</span> on canvas
                </div>
                <div>to create pane</div>
              </div>
            ) : (
              "switch to source\nto create panes"
            )
          }
          side="bottom"
        >
          <button
            onClick={action(() => {
              if (canCreatePane) {
                selectedToolAtom.value = ["pane", selectedPaneType];
              } else {
                setShowPopup((v) => !v);
              }
            })}
            className={classNames("flex items-center justify-center pl-3 pr-5")}
          >
            <NewPaneIcon />
            <span className="ml-1.5">{PaneTypeNames[selectedPaneType]}</span>
          </button>
        </Tooltip>
        <button
          onClick={() => {
            setShowPopup((v) => !v);
          }}
          className={classNames(
            "flex items-center absolute pl-0.5 pr-1 right-0 top-0 bottom-0 group2",
            {
              "focus:outline-none": showPopup,
            }
          )}
        >
          <FeatherIcon
            icon="chevron-down"
            size="sm"
            className={classNames(
              "transform transition-transform",
              showPopup ? "translate-y-0.5" : "group2-hover:translate-y-0.5"
            )}
          />
        </button>
        {showPopup ? (
          <PopperLayer
            reference={rootRef}
            render={({ state, completeTransitionExit }) => (
              <AnimatedPopout
                state={state}
                completeTransitionExit={completeTransitionExit}
                transformOrigin="top left"
              >
                {canCreatePane ? (
                  <AddPaneMenu
                    onChange={action((paneType) => {
                      setSelectedPaneType(paneType);
                      selectedToolAtom.value = ["pane", paneType];
                    })}
                    onClose={() => setShowPopup(false)}
                  />
                ) : (
                  <div className={AddPanePopupRootClassName}>
                    <ContextMenuItem
                      onClick={() => {
                        ParentComlinkAPI.switchLayoutId(undefined);
                        setShowPopup(false);
                      }}
                    >
                      switch to source to create panes
                    </ContextMenuItem>
                  </div>
                )}
              </AnimatedPopout>
            )}
            options={{
              placement: "bottom-start",
            }}
            onOutsideMouseDown={() => setShowPopup(false)}
            transitionExit={true}
          />
        ) : null}
      </div>
    );
  }
);
